/**
 * Edit a routing group: its terms, its caps, and its smart filters.
 *
 * The filter editor is shared with the destination-link form rather than
 * copied — see `SmartFilterEditor`, which also explains why it contains no
 * list of filter types or operators.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Button, Divider, Grid, Group, Modal, NumberInput, Select, Stack,
  Switch, Text, TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  useCampaignDetail, useDeleteRouteGroup, useEnums, useUpdateRouteGroup,
} from '../api/queries';
import {
  SmartFilters, asFilters, toStored, unfinishedListsReason, type SmartFilter,
} from './SmartFilterEditor';
import { useVocab } from '../vocab';

type CampaignDetail = NonNullable<ReturnType<typeof useCampaignDetail>['data']>;
type RouteGroup = CampaignDetail['groups'][number];

export function EditGroupModal({
  campaignId,
  group,
  onClose,
}: {
  campaignId: string | undefined;
  group: RouteGroup | null;
  onClose: () => void;
}) {
  const { data: enums } = useEnums();
  const v = useVocab();
  const save = useUpdateRouteGroup(campaignId);
  const remove = useDeleteRouteGroup(campaignId);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const [name, setName] = useState('');
  const [priority, setPriority] = useState<number>(1);
  const [method, setMethod] = useState('priority');
  const [deliveryCount, setDeliveryCount] = useState<number>(1);
  const [delay, setDelay] = useState<number>(0);
  const [weight, setWeight] = useState<number>(100);
  const [daily, setDaily] = useState<number>(0);
  const [weekly, setWeekly] = useState<number>(0);
  const [monthly, setMonthly] = useState<number>(0);
  const [filtersEnabled, setFiltersEnabled] = useState(true);
  const [andFilters, setAndFilters] = useState<SmartFilter[]>([]);
  const [orFilters, setOrFilters] = useState<SmartFilter[]>([]);

  // Re-seed whenever a different group is opened.
  useEffect(() => {
    if (!group) return;
    setName(group.name);
    setPriority(group.priority);
    setMethod(group.distribution_method);
    setDeliveryCount(group.delivery_count);
    setDelay(group.delay_seconds);
    setWeight(group.group_weight);
    setDaily(group.caps.daily_cap);
    setWeekly(group.caps.weekly_cap);
    setMonthly(group.caps.monthly_cap);
    setFiltersEnabled(group.filters_enabled);
    setAndFilters(asFilters(group.smart_filters, 'and'));
    setOrFilters(asFilters(group.smart_filters, 'or'));
  }, [group]);

  const cannotSave = unfinishedListsReason([...andFilters, ...orFilters]);

  async function submit() {
    if (!group) return;
    try {
      await save.mutateAsync({
        groupId: group.id,
        body: {
          name,
          priority,
          distribution_method: method,
          delivery_count: deliveryCount,
          delay_seconds: delay,
          group_weight: weight,
          group_daily_cap: daily,
          group_weekly_cap: weekly,
          group_monthly_cap: monthly,
          filters_enabled: filtersEnabled,
          smart_filters: toStored(andFilters, orFilters),
        },
      });
      notifications.show({ color: 'teal', title: 'Group updated', message: `${name} saved.` });
      onClose();
    } catch (e) {
      notifications.show({ color: 'red', title: 'Save failed', message: (e as Error).message });
    }
  }

  async function doDelete() {
    if (!group) return;
    try {
      await remove.mutateAsync(group.id);
      notifications.show({
        color: 'teal',
        title: 'Group deleted',
        message: `${group.name} removed. Its ${v.many('destination')} still exist and can be linked elsewhere.`,
      });
      setConfirmingDelete(false);
      onClose();
    } catch (e) {
      notifications.show({ color: 'red', title: 'Delete failed', message: (e as Error).message });
    }
  }

  return (
    <Modal opened={group !== null} onClose={onClose} title={`Edit ${v.one('routeGroup')}`} size="xl">
      <Stack>
        <Grid gutter="sm">
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput label="Group name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
          </Grid.Col>
          <Grid.Col span={{ base: 6, sm: 3 }}>
            <NumberInput label="Priority" min={1} value={priority} onChange={(v) => setPriority(Number(v) || 1)} />
          </Grid.Col>
          <Grid.Col span={{ base: 6, sm: 3 }}>
            <Select
              label="Distribution method"
              /* Served, so this can never offer a method the engine lacks. */
              data={(enums?.distribution_methods ?? []).map((m) => ({ value: m.value, label: m.label }))}
              value={method}
              allowDeselect={false}
              onChange={(v) => v && setMethod(v)}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 6, sm: 4 }}>
            <NumberInput
              label="Delivery count"
              description={`How many ${v.many('destination')} may take the ${v.one('lead')}`}
              min={1}
              value={deliveryCount}
              onChange={(v) => setDeliveryCount(Number(v) || 1)}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 6, sm: 4 }}>
            <NumberInput label="Delay (seconds)" min={0} value={delay} onChange={(v) => setDelay(Number(v) || 0)} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <NumberInput
              label={`${v.One('routeGroup')} weight (%)`}
              description={`Splits traffic between ${v.many('routeGroup')} at the same priority`}
              min={0}
              value={weight}
              onChange={(v) => setWeight(Number(v) || 0)}
            />
          </Grid.Col>
        </Grid>

        <Divider label={`${v.One('routeGroup')} caps (0 means unlimited)`} labelPosition="left" />
        <Grid gutter="sm">
          <Grid.Col span={4}>
            <NumberInput label="Daily" min={0} value={daily} onChange={(v) => setDaily(Number(v) || 0)} />
          </Grid.Col>
          <Grid.Col span={4}>
            <NumberInput label="Weekly" min={0} value={weekly} onChange={(v) => setWeekly(Number(v) || 0)} />
          </Grid.Col>
          <Grid.Col span={4}>
            <NumberInput label="Monthly" min={0} value={monthly} onChange={(v) => setMonthly(Number(v) || 0)} />
          </Grid.Col>
        </Grid>

        <Divider label="Smart filters" labelPosition="left" />
        <Switch
          label="Filters enabled"
          description="Off means every filter below is ignored and nothing is screened out"
          checked={filtersEnabled}
          onChange={(e) => setFiltersEnabled(e.currentTarget.checked)}
        />
        <SmartFilters
          andFilters={andFilters}
          orFilters={orFilters}
          enabled={filtersEnabled}
          setAndFilters={setAndFilters}
          setOrFilters={setOrFilters}
          campaignIds={campaignId ? [campaignId] : undefined}
          previewCampaignId={campaignId}
        />

        <Divider />
        <Group justify="space-between">
          <Button variant="subtle" color="red" onClick={() => setConfirmingDelete(true)}>
            {`Delete ${v.one('routeGroup')}`}
          </Button>
          <Group>
            {cannotSave && <Text size="xs" c="dimmed" data-testid="save-blocked">{cannotSave}</Text>}
            <Button variant="subtle" onClick={onClose}>Cancel</Button>
            <Button loading={save.isPending} disabled={!name.trim() || !!cannotSave} onClick={submit}>
              {`Save ${v.one('routeGroup')}`}
            </Button>
          </Group>
        </Group>

        <Modal
          opened={confirmingDelete}
          onClose={() => setConfirmingDelete(false)}
          title={`Delete ${group?.name ?? `this ${v.one('routeGroup')}`}?`}
          zIndex={1002}
        >
          <Stack>
            <Alert color="red" variant="light" title={`${v.Many('lead')} stop routing through this tier`}>
              <Text size="sm">
                {`The tier and its ${group?.destinations.length ?? 0} ${v.one('destination')} link${group?.destinations.length === 1 ? '' : 's'} are removed. ${v.Many('lead')} that would have been placed here fall through to the next tier, or go undelivered if there is none.`}
              </Text>
            </Alert>
            <Text size="sm">
              {`The ${v.many('destination')} themselves are not deleted and stay available to other ${v.many('routeGroup')}.`}
            </Text>
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setConfirmingDelete(false)}>Cancel</Button>
              <Button color="red" loading={remove.isPending} onClick={doDelete}>{`Delete ${v.one('routeGroup')}`}</Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </Modal>
  );
}
